import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Heart,
  Play,
} from "lucide-react";

import "./WeddingsEvents.css";

const galleryImages = [
  "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1400&q=85",
  "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?auto=format&fit=crop&w=1400&q=85",
  "https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=1400&q=85",
  "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=1400&q=85",
];

const galleryLabels = [
  "A beautiful beginning",
  "Elegant celebrations",
  "Moments to remember",
  "Love, laughter & memories",
];

const eventGalleryImages = [
  "https://images.unsplash.com/photo-1464349153735-7db50ed83c84?auto=format&fit=crop&w=1600&q=90",
  "https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1600&q=90",
  "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1600&q=90",
  "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&w=1600&q=90",
  "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=1600&q=90",
  "https://images.unsplash.com/photo-1505236858219-8359eb29e329?auto=format&fit=crop&w=1600&q=90",
  "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=1600&q=90",
  "https://images.unsplash.com/photo-1507504031003-b417219a0fde?auto=format&fit=crop&w=1600&q=90",
];

const packages = [
  {
    name: "Gold Wedding Package",
    shortName: "Gold",
    price: "£70 per person",
    intro: "A beautiful foundation for an elegant and memorable wedding celebration.",
    sections: [
      {
        title: "Food & Drink",
        items: [
          "Welcome drink of Prosecco",
          "Prosecco toast",
          "3-course wedding breakfast",
        ],
      },
      {
        title: "Décor & Styling",
        items: [
          "Professional wedding décor",
          "Dressed chairs, sashes & table runners",
          "Top table swag & cake table styling",
          "Wedding cake stand",
        ],
      },
      {
        title: "Service & Extras",
        items: [
          "Dedicated Events Manager",
          "Fully staffed bar",
          "Complimentary parking",
          "Transport around the course for photos",
        ],
      },
    ],
  },
  {
    name: "Platinum Wedding Package",
    shortName: "Platinum",
    price: "£105 per person",
    featured: true,
    intro: "Everything included in Gold, plus enhanced dining and an overnight stay for the newlywed couple.",
    includes: "Includes all Gold Package features",
    sections: [
      {
        title: "Food & Drink",
        items: [
          "Welcome drink of Prosecco",
          "Prosecco toast",
          "3-course wedding breakfast",
          "Traditional British hog roast with sides & salads",
          "Vegetarian option available",
        ],
      },
      {
        title: "Accommodation & Perks",
        items: [
          "Overnight stay for the newlywed couple",
        ],
      },
      {
        title: "Gold Features Included",
        items: [
          "Professional wedding décor",
          "Dressed chairs, sashes & table runners",
          "Top table swag & cake table styling",
          "Wedding cake stand",
          "Dedicated Events Manager & fully staffed bar",
          "Complimentary parking & photo transport",
        ],
      },
    ],
  },
  {
    name: "Diamond Wedding Package",
    shortName: "Diamond",
    price: "£150 per person",
    intro: "Our most complete wedding experience, adding a second full meal and accommodation for additional overnight guests.",
    includes: "Includes all Gold & Platinum Package features",
    sections: [
      {
        title: "Food & Drink — Two Full Meals",
        items: [
          "Traditional British hog roast with sides & salads",
          "Formal evening dinner",
          "Starters — traditional options",
          "Mains — classic British dishes",
          "Desserts — artisan choices",
          "Vegetarian options available",
        ],
      },
      {
        title: "Accommodation & Perks",
        items: [
          "Overnight stay for the newlywed couple",
          "Accommodation for 20 additional guests",
        ],
      },
      {
        title: "Included Wedding Features",
        items: [
          "All Gold Package décor & styling",
          "Dedicated Events Manager & fully staffed bar",
          "Complimentary parking & transport for photos",
        ],
      },
    ],
  },
];

/* =========================================================
   ORIGINAL FLOWER / LEAF
========================================================= */

function SideFloral({
  colour = "green",
  side = "left",
  position = "middle",
}) {
  return (
    <div
      className={`side-floral side-floral-${colour} side-floral-${side} side-floral-${position}`}
      aria-hidden="true"
    >
      ❧
    </div>
  );
}

/* =========================================================
   SECTION DIVIDER
   FINE HEART / WEDDING FILIGREE
========================================================= */

function SectionDivider() {
  return (
    <div className="section-divider" aria-hidden="true">
      <svg
        className="section-divider-line"
        viewBox="0 0 1000 38"
        preserveAspectRatio="none"
      >
        {/* Fine line flowing in from both sides */}
        <path
          className="divider-stroke"
          d="
            M 0 19
            C 220 19, 360 19, 430 19
            C 455 19, 468 19, 482 16
            C 490 14, 496 12, 500 9

            M 500 9
            C 504 12, 510 14, 518 16
            C 532 19, 545 19, 570 19
            C 640 19, 780 19, 1000 19
          "
        />

        {/* Soft glow around the heart */}
        <path
          className="divider-heart-glow"
          d="
            M 500 31
            C 496 26, 484 20, 486 14
            C 488 9, 495 10, 500 15
            C 505 10, 512 9, 514 14
            C 516 20, 504 26, 500 31
          "
        />

        {/* Central heart / wedding filigree */}
        <path
          className="divider-heart"
          d="
            M 500 33

            C 496 28, 483 21, 484 14
            C 485 8, 492 7, 500 15

            C 508 7, 515 8, 516 14
            C 517 21, 504 28, 500 33

            M 484 18
            C 476 13, 468 14, 466 18
            C 464 22, 469 25, 476 25

            M 516 18
            C 524 13, 532 14, 534 18
            C 536 22, 531 25, 524 25

            M 466 18
            C 457 17, 451 18, 445 19

            M 534 18
            C 543 17, 549 18, 555 19
          "
        />

        {/* Tiny four-point sparkles */}
        <path
          className="divider-sparkle"
          d="
            M 120 19
            L 122 17
            L 124 19
            L 122 21
            Z

            M 876 19
            L 878 17
            L 880 19
            L 878 21
            Z

            M 405 19
            L 406 18
            L 407 19
            L 406 20
            Z

            M 593 19
            L 594 18
            L 595 19
            L 594 20
            Z
          "
        />
      </svg>
    </div>
  );
}

export default function WeddingsEvents() {
  const [currentImage, setCurrentImage] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState("");
  const [selectedEvent, setSelectedEvent] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    const data = {
      name: formData.get("name"),
      email: formData.get("email"),
      event_type: formData.get("event_type"),
      wedding_package: formData.get("wedding_package"),
      date: formData.get("date"),
      guest_count: Number(formData.get("guest_count")),
      message: formData.get("message") || "",
    };

    try {
      const response = await fetch(
        "https://my-website-production-d666.up.railway.app/api/forms/events",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || "Something went wrong.");
      }

      alert("Thank you! Your enquiry has been sent successfully.");
      form.reset();
    } catch (error) {
      console.error("Form submission error:", error);
      alert("Sorry, there was a problem sending your enquiry. Please try again.");
    }
  };

  const scrollToEnquiry = () => {
    window.setTimeout(() => {
      document.getElementById("enquire")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  };

  const handlePackageEnquiry = (packageName) => {
    setSelectedPackage(packageName);
    scrollToEnquiry();
  };

  const handleEventEnquiry = (eventType) => {
    setSelectedEvent(eventType);
    scrollToEnquiry();
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage(
        (previous) => (previous + 1) % galleryImages.length
      );
    }, 3000);

    return () => clearInterval(timer);
  }, []);

  const previousImage = () => {
    setCurrentImage(
      (currentImage - 1 + galleryImages.length) %
        galleryImages.length
    );
  };

  const nextImage = () => {
    setCurrentImage(
      (currentImage + 1) % galleryImages.length
    );
  };

  return (
    <main className="weddings-page">

      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="weddings-hero">

        <div className="hero-overlay" />

        <div className="hero-heart-frame" aria-hidden="true">
          <svg
            viewBox="0 0 930 680"
            preserveAspectRatio="none"
          >
            <path
              d="
                M 465 625
                C 430 585, 150 420, 105 235
                C 65 75, 255 28, 350 125
                C 405 180, 435 215, 465 250
                C 495 215, 525 180, 580 125
                C 675 28, 865 75, 825 235
                C 780 420, 500 585, 465 625
              "
            />
          </svg>
        </div>

        <div className="hero-content">

          <span className="luxury-overline">
            Weddings & Special Events
          </span>

          <h1>
            Your perfect day,
            <br />
            <em>beautifully celebrated.</em>
          </h1>

          <p>
            A truly special setting for weddings,
            celebrations and unforgettable occasions
            at The Gower Golf Club.
          </p>

          <div className="hero-buttons">

            <a
              href="#packages"
              className="luxury-button luxury-button-gold"
            >
              View Wedding Packages
              <ArrowRight size={16} strokeWidth={1.5} />
            </a>

            <a
              href="#enquire"
              className="luxury-button luxury-button-outline"
            >
              Enquire About Your Event
            </a>

          </div>

        </div>

        <div className="hero-bottom">

          <span>THE GOWER GOLF CLUB</span>

          <span className="hero-line" />

          <span>SWANSEA · SOUTH WALES</span>

        </div>

      </section>


      {/* =====================================================
          EXPERIENCE THE MAGIC
      ===================================================== */}

      <section className="experience-section">

        <SideFloral
          colour="green"
          side="left"
          position="upper"
        />

        <SideFloral
          colour="green"
          side="right"
          position="lower"
        />

        <div className="experience-inner">

          <div className="experience-image">

            <img
              src="https://images.unsplash.com/photo-1507504031003-b417219a0fde?auto=format&fit=crop&w=1400&q=85"
              alt="Beautiful celebration setting at The Gower Golf Club"
            />

            <div className="experience-image-overlay" />

            <button
              className="video-play"
              aria-label="Play event video"
            >
              <Play
                size={25}
                fill="currentColor"
                strokeWidth={1}
              />
            </button>

            <span className="video-label">
              WATCH OUR EVENT FILM
            </span>

          </div>

          <div className="experience-copy">

            <span className="luxury-overline">
              Experience the magic
            </span>

            <h2>
              A place like
              <br />
              <em>no other.</em>
            </h2>

            <div className="gold-rule" />

            <p>
              Take a closer look at the setting,
              atmosphere and beautiful surroundings
              that make The Gower Golf Club such a
              special place for weddings, celebrations
              and private events.
            </p>

            <button className="luxury-button luxury-button-gold">

              <Play
                size={15}
                fill="currentColor"
                strokeWidth={1.2}
              />

              Watch Our Video

            </button>

          </div>

        </div>

      </section>


      {/* =====================================================
          CAPTURED FOREVER
      ===================================================== */}

      <section className="captured-section">

        <div className="captured-inner">

          <div className="captured-copy">

            <span className="luxury-overline">
              Captured forever
            </span>

            <h2>
              Beautiful
              <br />
              <em>moments.</em>
            </h2>

            <div className="gold-rule" />

            <p>
              Imagine your celebration surrounded
              by the natural beauty of the Gower
              Peninsula, with unforgettable
              surroundings and a venue designed
              for life's most important moments.
            </p>

            <p>
              From the first arrival to the final
              farewell, The Gower Golf Club provides
              a beautiful backdrop for weddings,
              parties and special occasions.
            </p>

            <a
              href="#packages"
              className="luxury-button luxury-button-gold"
            >
              Discover Our Packages
              <ArrowRight size={16} strokeWidth={1.5} />
            </a>

          </div>


          <div className="arch-slider">

            <div className="arch-image">

              {galleryImages.map((image, index) => (
                <img
                  key={image}
                  src={image}
                  alt={galleryLabels[index]}
                  className={
                    index === currentImage ? "active" : ""
                  }
                />
              ))}

              <div className="arch-caption">

                <Heart
                  size={17}
                  strokeWidth={1.2}
                />

                <span>
                  {galleryLabels[currentImage]}
                </span>

              </div>

            </div>


            <button
              className="slider-arrow slider-left"
              onClick={previousImage}
              aria-label="Previous photo"
            >
              <ChevronLeft
                size={22}
                strokeWidth={1.2}
              />
            </button>


            <button
              className="slider-arrow slider-right"
              onClick={nextImage}
              aria-label="Next photo"
            >
              <ChevronRight
                size={22}
                strokeWidth={1.2}
              />
            </button>

          </div>


          <div className="slider-dots">

            {galleryImages.map((_, index) => (
              <button
                key={index}
                className={
                  index === currentImage ? "active" : ""
                }
                onClick={() => setCurrentImage(index)}
                aria-label={`Show photo ${index + 1}`}
              />
            ))}

          </div>

        </div>

        <SectionDivider />

      </section>


      {/* =====================================================
          WEDDING PACKAGES
      ===================================================== */}

      <section
        id="packages"
        className="packages-section"
      >

        <SideFloral
          colour="gold"
          side="left"
          position="upper"
        />

        <SideFloral
          colour="green"
          side="right"
          position="lower"
        />

        <div className="section-heading packages-heading">

          <span className="luxury-overline">
            Wedding packages
          </span>

          <h2>
            Choose your
            <br />
            <em>perfect celebration.</em>
          </h2>

          <div className="gold-rule" />

          <p>
            Beautifully considered wedding packages designed to give you
            an elegant celebration, with the flexibility to make your day
            uniquely yours.
          </p>

        </div>

        <div className="packages-grid">

          {packages.map((pkg) => (

            <article
              className={`package-card package-card-${pkg.shortName.toLowerCase()}${
                pkg.featured ? " featured" : ""
              }`}
              key={pkg.name}
            >

              <div className="package-card-header">

                <span className="package-small">
                  {pkg.shortName} Wedding Package
                </span>

                <h3>{pkg.shortName}</h3>

                <div className="package-price">
                  {pkg.price}
                </div>

              </div>

              {pkg.includes && (
                <div className="package-includes">
                  {pkg.includes}
                </div>
              )}

              <p className="package-intro">
                {pkg.intro}
              </p>

              <div className="package-content">

                {pkg.sections.map((section) => (

                  <div
                    className="package-section"
                    key={section.title}
                  >

                    <h4>{section.title}</h4>

                    <ul>
                      {section.items.map((item) => (
                        <li key={item}>
                          <span aria-hidden="true">✦</span>
                          {item}
                        </li>
                      ))}
                    </ul>

                  </div>

                ))}

              </div>

              <button
                type="button"
                className="package-button"
                onClick={() => handlePackageEnquiry(pkg.name)}
              >
                <span>Enquire about {pkg.shortName}</span>
                <ArrowRight size={16} strokeWidth={1.5} />
              </button>

            </article>

          ))}

        </div>

        <SectionDivider />

      </section>


      {/* =====================================================
          CELEBRATIONS VIDEO
      ===================================================== */}

      <section className="video-section">

        <SideFloral
          colour="gold"
          side="left"
          position="middle"
        />

        <SideFloral
          colour="gold"
          side="right"
          position="lower"
        />

        <div className="video-inner">

          <div className="video-copy">

            <span className="luxury-overline">
              Your celebration
            </span>

            <h2>
              Every occasion,
              <br />
              <em>beautifully yours.</em>
            </h2>

            <div className="gold-rule" />

            <p>
              From birthdays and anniversaries
              to special celebrations and corporate
              occasions, discover how your event
              could look at The Gower Golf Club.
            </p>

            <button className="luxury-button luxury-button-gold">

              <Play
                size={15}
                fill="currentColor"
                strokeWidth={1.2}
              />

              Watch Our Video

            </button>

          </div>


          <div className="video-placeholder">

            <img
              src="https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?auto=format&fit=crop&w=1400&q=85"
              alt="Beautiful event celebration at The Gower Golf Club"
            />

            <div className="video-tint" />

            <button
              className="video-play"
              aria-label="Play celebration film"
            >
              <Play
                size={25}
                fill="currentColor"
                strokeWidth={1}
              />
            </button>

            <span className="video-label">
              WATCH OUR CELEBRATION FILM
            </span>

          </div>

        </div>

        <SectionDivider />

      </section>


      {/* =====================================================
          INFINITE EVENT GALLERY
      ===================================================== */}

      <section
        className="event-gallery-loop"
        aria-label="Event gallery"
      >

        <div className="event-gallery-track">

          {eventGalleryImages.map((image, index) => (
            <div
              className="event-gallery-item"
              key={`event-gallery-${index}`}
            >
              <img src={image} alt="" />
            </div>
          ))}


          {eventGalleryImages.map((image, index) => (
            <div
              className="event-gallery-item"
              key={`event-gallery-copy-${index}`}
              aria-hidden="true"
            >
              <img src={image} alt="" />
            </div>
          ))}

        </div>

        <SectionDivider />

      </section>


      {/* =====================================================
          CELEBRATIONS FOR EVERY OCCASION
      ===================================================== */}

      <section className="other-events" id="other-events">

        <SectionDivider />

        <SideFloral
          colour="gold"
          side="left"
          position="lower"
        />

        <SideFloral
          colour="green"
          side="right"
          position="upper"
        />

        <div className="other-events-inner">

          <div className="section-heading other-events-heading">

            <h2>
              Celebrations for
              <br />
              <em>every occasion.</em>
            </h2>

            <p>
              From milestone birthdays and anniversaries to private parties,
              corporate gatherings and special occasions, our team can help
              create an event that feels truly yours.
            </p>

          </div>

          <div className="occasion-grid">

            <article className="occasion-card">

              <div className="occasion-image-wrap">
                <img
                  src="https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=1200&q=85"
                  alt="Birthday celebration with friends"
                />
              </div>

              <div className="occasion-content">
                <span>01 · Birthdays</span>
                <h3>Birthdays &amp; Celebrations</h3>
                <p>
                  From milestone birthdays to family gatherings, we help you
                  create memorable moments with food, drinks and a beautifully
                  prepared setting.
                </p>

                <button
                  type="button"
                  className="occasion-link"
                  onClick={() => handleEventEnquiry("Birthday")}
                >
                  Enquire about this event
                  <ArrowRight size={16} strokeWidth={1.5} />
                </button>
              </div>

            </article>


            <article className="occasion-card occasion-card-featured">

              <div className="occasion-image-wrap">
                <img
                  src="https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&w=1200&q=85"
                  alt="Elegant anniversary celebration"
                />
              </div>

              <div className="occasion-content">
                <span>02 · Special occasions</span>
                <h3>Anniversaries &amp; Special Occasions</h3>
                <p>
                  Celebrate the moments that matter, from anniversaries and
                  family occasions to elegant private gatherings designed
                  around your plans.
                </p>

                <button
                  type="button"
                  className="occasion-link"
                  onClick={() => handleEventEnquiry("Anniversary")}
                >
                  Enquire about this event
                  <ArrowRight size={16} strokeWidth={1.5} />
                </button>
              </div>

            </article>


            <article className="occasion-card">

              <div className="occasion-image-wrap">
                <img
                  src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1200&q=85"
                  alt="Corporate event and presentation"
                />
              </div>

              <div className="occasion-content">
                <span>03 · Business &amp; social</span>
                <h3>Private, Corporate &amp; Social Events</h3>
                <p>
                  From business meetings and corporate events to private parties
                  and social gatherings, our versatile spaces and experienced
                  team help create a seamless experience.
                </p>

                <button
                  type="button"
                  className="occasion-link"
                  onClick={() => handleEventEnquiry("Corporate Event")}
                >
                  Enquire about this event
                  <ArrowRight size={16} strokeWidth={1.5} />
                </button>
              </div>

            </article>

          </div>

        </div>

      </section>


      {/* =====================================================
          ENQUIRY
      ===================================================== */}

      <section
        id="enquire"
        className="enquiry-section"
      >

        <SectionDivider />

        <SideFloral
          colour="gold"
          side="left"
          position="lower"
        />

        <SideFloral
          colour="gold"
          side="right"
          position="upper"
        />

        <div className="enquiry-inner">

          <span className="luxury-overline">
            Let's create something special
          </span>

          <h2>
            Start planning
            <br />
            <em>your day.</em>
          </h2>

          <p>
            Tell us a little about your wedding
            or event and our team will be happy
            to help.
          </p>


          <div className="form-glow-frame">

            <form className="wedding-form" onSubmit={handleSubmit}>

              <div className="form-row">

                <div className="form-field">

                  <label htmlFor="name">
                    Your Name
                  </label>

                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Your name"
                  />

                </div>


                <div className="form-field">

                  <label htmlFor="email">
                    Email Address
                  </label>

                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Your email"
                  />

                </div>

              </div>


              <div className="form-row">

                <div className="form-field">

                  <label htmlFor="event-type">
                    Event Type
                  </label>

                  <select
                      id="event-type"
                      name="event_type"
                      value={selectedEvent}
                      onChange={(event) => setSelectedEvent(event.target.value)}
                    >

                    <option value="">
                      Select an event
                    </option>

                    <option>Wedding</option>
                    <option>Birthday</option>
                    <option>Anniversary</option>
                    <option>Special Event</option>
                    <option>Private Celebration</option>
                    <option>Corporate Event</option>

                  </select>

                </div>

                <div className="form-field">

                  <label htmlFor="wedding-package">
                    Wedding Package
                  </label>

                  <select
                    id="wedding-package"
                    name="wedding_package"
                    value={selectedPackage}
                    onChange={(event) => setSelectedPackage(event.target.value)}
                  >

                    <option value="">
                      Select a package
                    </option>

                    {packages.map((pkg) => (
                      <option key={pkg.name} value={pkg.name}>
                        {pkg.name}
                      </option>
                    ))}

                    <option value="Bespoke / Other">
                      Bespoke / Other
                    </option>

                  </select>

                </div>

              </div>

              <div className="form-row">

                <div className="form-field">

                  <label htmlFor="event-date">
                    Preferred Date
                  </label>

                  <input
                    id="event-date"
                    name="date"
                    type="date"
                  />

                </div>

                <div className="form-field">

                  <label htmlFor="guest-numbers">
                    Approximate Guests
                  </label>

                  <input
                    id="guest-numbers"
                    name="guest_count"
                    type="number"
                    min="1"
                    placeholder="Number of guests"
                  />

                </div>

              </div>

              <div className="form-field">

                <label htmlFor="details">
                  Tell us about your plans
                </label>

                <textarea
                  id="details"
                  name="message"
                  rows="5"
                  placeholder="Tell us about your wedding or event..."
                />

              </div>


              <button
                type="submit"
                className="luxury-button luxury-button-gold form-submit"
              >

                Enquire About Your Event

                <ArrowRight
                  size={16}
                  strokeWidth={1.5}
                />

              </button>

            </form>

          </div>

        </div>

      </section>

    </main>
  );
}
